import { ListChecks, Workflow } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExtractionRunCard } from '@/components/documents/ExtractionRunCard'
import { PipelineStatsStrip } from '@/components/documents/PipelineStatsStrip'
import { PageShell } from '@/components/layout/PageShell'
import { FilterChip } from '@/components/ui/filter-chip'
import { ExtractionRunDetail } from '@/features/documents/ExtractionRunDetail'
import type { StatusFilter } from '@/features/documents/extraction-run-format'
import { useExtractionRuns } from '@/features/documents/hooks/useExtractionRuns'
import { useExtractionRunsView } from '@/features/documents/hooks/useExtractionRunsView'
import { cn } from '@/utils/cn'
import { useCaseContext } from '../../CaseContext'

/** How often the runs are read again while one is going. */
const RUNS_POLL_MS = 5000

export default function DocumentsPipelinesPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [filter, setFilter] = useState<StatusFilter>('all')
  const { data: extractionRuns = [], isLoading: loadingRuns } = useExtractionRuns(
    caseId,
    RUNS_POLL_MS
  )
  const { counts, filteredRuns } = useExtractionRunsView(extractionRuns, filter)

  const FILTERS: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: t('extraction.all') },
    { key: 'running', label: t('extraction.running') },
    { key: 'complete', label: t('extraction.complete') },
    { key: 'partial', label: t('extraction.partial') },
    { key: 'failed', label: t('extraction.failed') },
  ]

  return (
    <PageShell
      layout="full"
      icon={Workflow}
      title={t('cases.pipelines.title')}
      summary={t('cases.pipelines.summary')}
    >
      <div className="flex flex-1 overflow-hidden bg-background">
        <div
          className={cn(
            'h-full overflow-auto px-4 pb-6 space-y-4 transition-all md:px-6',
            // Phone: the run detail takes the whole screen; its close button brings the list back.
            selectedRunId ? 'max-md:hidden md:w-1/2' : 'w-full'
          )}
        >
          {loadingRuns ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full size-8 border-b-2 border-primary" />
            </div>
          ) : extractionRuns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ListChecks className="h-12 w-12 text-muted-foreground/40 mb-3" />
              <h3 className="text-sm font-semibold text-foreground mb-1">No extraction runs yet</h3>
              <p className="text-xs text-muted-foreground max-w-sm">
                Select documents on the Documents tab and run extraction to see progress here.
              </p>
            </div>
          ) : (
            <>
              <PipelineStatsStrip runs={extractionRuns} compact={!!selectedRunId} />

              <div className="scrollbar-none flex items-center gap-2 flex-wrap max-md:-mx-4 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-4 max-md:[&>*]:shrink-0">
                {FILTERS.map(f => (
                  <FilterChip
                    key={f.key}
                    label={f.label}
                    count={counts[f.key]}
                    active={filter === f.key}
                    onClick={() => setFilter(f.key)}
                  />
                ))}
              </div>

              {filteredRuns.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-8">
                  No runs match this filter.
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredRuns.map(run => (
                    <ExtractionRunCard
                      key={run.id}
                      run={run}
                      selected={run.id === selectedRunId}
                      onClick={() => setSelectedRunId(run.id === selectedRunId ? null : run.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {selectedRunId && (
          <div className="w-full border-border overflow-hidden md:w-1/2 md:border-l">
            <ExtractionRunDetail
              caseId={caseId}
              runId={selectedRunId}
              onClose={() => setSelectedRunId(null)}
            />
          </div>
        )}
      </div>
    </PageShell>
  )
}
